import { ReadinessModel, RouteClass } from "../contracts/common.js";
import { contractError } from "../contracts/errors.js";
import { err, ok } from "../contracts/result.js";
import { failContract } from "../internal/errors.js";
import { expectNumber, expectObject, expectOptionalNumber, expectOptionalString, expectString, readField, readOptionalField, } from "../internal/json.js";
import { requestResponseResult } from "../internal/transport.js";
const STREAM_ROUTE = {
    method: "GET",
    name: "stream",
    path: "/api/v1/stream",
    readinessModel: ReadinessModel.LIVE,
    routeClass: RouteClass.LIVE,
};
/**
 * Public route descriptors for NDJSON stream routes.
 */
export const streamRouteDescriptors = {
    events: STREAM_ROUTE,
};
function parseMempoolPayload(value, path) {
    const object = expectObject(value, path);
    return {
        sequence: expectNumber(readField(object, "sequence", path), `${path}.sequence`),
        txid: expectString(readField(object, "txid", path), `${path}.txid`),
        rawtx: expectString(readField(object, "rawtx", path), `${path}.rawtx`),
    };
}
function parseChainLockPayload(value, path) {
    const object = expectObject(value, path);
    const sequence = expectOptionalNumber(readOptionalField(object, "sequence"), `${path}.sequence`);
    const blockHeight = expectOptionalNumber(readOptionalField(object, "blockHeight"), `${path}.blockHeight`);
    return {
        ...(sequence == null ? {} : { sequence }),
        blockHash: expectString(readField(object, "blockHash", path), `${path}.blockHash`),
        ...(blockHeight == null ? {} : { blockHeight }),
        rawChainlock: expectString(readField(object, "rawChainlock", path), `${path}.rawChainlock`),
    };
}
function parseGovernanceObjectPayload(value, path) {
    const object = expectObject(value, path);
    const sequence = expectOptionalNumber(readOptionalField(object, "sequence"), `${path}.sequence`);
    const objectHash = expectOptionalString(readOptionalField(object, "objectHash"), `${path}.objectHash`);
    return {
        ...(sequence == null ? {} : { sequence }),
        ...(objectHash == null ? {} : { objectHash }),
        payload: readField(object, "payload", path),
    };
}
function parseGovernanceVotePayload(value, path) {
    const object = expectObject(value, path);
    const sequence = expectOptionalNumber(readOptionalField(object, "sequence"), `${path}.sequence`);
    const objectHash = expectOptionalString(readOptionalField(object, "objectHash"), `${path}.objectHash`);
    const voteHash = expectOptionalString(readOptionalField(object, "voteHash"), `${path}.voteHash`);
    return {
        ...(sequence == null ? {} : { sequence }),
        ...(objectHash == null ? {} : { objectHash }),
        ...(voteHash == null ? {} : { voteHash }),
        payload: readField(object, "payload", path),
    };
}
function parseNetworkPayload(value, path) {
    const object = expectObject(value, path);
    return {
        payload: readField(object, "payload", path),
    };
}
function parseStreamEvent(value, path) {
    const object = expectObject(value, path);
    const type = expectString(readField(object, "type", path), `${path}.type`);
    const ts = expectNumber(readField(object, "ts", path), `${path}.ts`);
    switch (type) {
        case "mempool_tx":
            return {
                type,
                ts,
                payload: parseMempoolPayload(readField(object, "payload", path), `${path}.payload`),
            };
        case "chain_lock":
            return {
                type,
                ts,
                payload: parseChainLockPayload(readField(object, "payload", path), `${path}.payload`),
            };
        case "governance_object":
            return {
                type,
                ts,
                payload: parseGovernanceObjectPayload(readField(object, "payload", path), `${path}.payload`),
            };
        case "governance_vote":
            return {
                type,
                ts,
                payload: parseGovernanceVotePayload(readField(object, "payload", path), `${path}.payload`),
            };
        case "network":
            return {
                type,
                ts,
                payload: parseNetworkPayload(readField(object, "payload", path), `${path}.payload`),
            };
        case "heartbeat":
            return {
                type,
                ts,
            };
        default:
            return failContract(`${path}.type`, `unsupported stream event type ${type}`);
    }
}
async function* decodeNdjson(body) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
        const chunk = await reader.read();
        if (chunk.done) {
            break;
        }
        buffer += decoder.decode(chunk.value, { stream: true });
        let newlineIndex = buffer.indexOf("\n");
        while (newlineIndex >= 0) {
            const line = buffer.slice(0, newlineIndex).trim();
            buffer = buffer.slice(newlineIndex + 1);
            if (line.length > 0) {
                yield parseStreamEvent(JSON.parse(line), "$");
            }
            newlineIndex = buffer.indexOf("\n");
        }
    }
    const tail = `${buffer}${decoder.decode()}`.trim();
    if (tail.length > 0) {
        yield parseStreamEvent(JSON.parse(tail), "$");
    }
}
/**
 * Build the live stream route family for one client transport context.
 */
export function buildStreamRoutes(context) {
    return {
        async streamEvents() {
            const responseResult = await requestResponseResult(context, STREAM_ROUTE, STREAM_ROUTE.path);
            const response = responseResult.value();
            if (response == null) {
                return err(responseResult.errorOr(contractError(STREAM_ROUTE.path, "stream request failed")));
            }
            const contentType = response.headers.get("content-type");
            if (!contentType?.includes("application/x-ndjson")) {
                return err(contractError(STREAM_ROUTE.path, "expected application/x-ndjson response"));
            }
            const body = response.body;
            if (body == null) {
                return err(contractError(STREAM_ROUTE.path, "stream response body is missing"));
            }
            return ok(decodeNdjson(body));
        },
    };
}
//# sourceMappingURL=stream.js.map